/**
 * Unified Media Configuration
 * Single source of truth for all media loading settings
 */

// Re-export MediaPriority type for use in components
export type { MediaPriorityLevel } from '@/lib/graphql/schema';
export type MediaPriority = 'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW' | 'BACKGROUND';

/**
 * Media Cache Version
 * Increment when media handlers or API changes to force cache invalidation
 * Used in URL parameters (?v=vX) to bypass stale caches
 * 
 * v5: Fixed duplicate video downloads by removing double-fetch in mediaCache 
 *     and adding deduplication in BackgroundVideo
 */
export const MEDIA_CACHE_VERSION = 'v5';

/**
 * Video preload strategies based on media priority
 * CRITICAL: Hero section - preload everything for instant playback
 * HIGH: Above fold - preload metadata only
 * NORMAL: Mid-page - no preload, lazy load
 * LOW/BACKGROUND: Below fold - no preload, paused until visible
 */
export type VideoPreloadStrategy = 'auto' | 'metadata' | 'none';

/**
 * Map MediaPriority to video preload strategy
 * This determines how aggressively the browser loads video data
 * OPTIMIZED: CRITICAL videos use 'metadata' + programmatic load() for better control
 */
export function getPreloadStrategyForPriority(priority: MediaPriority | boolean | undefined): VideoPreloadStrategy {
  // Handle legacy boolean priority (true = high priority)
  if (typeof priority === 'boolean') {
    return priority ? 'metadata' : 'metadata'; // Changed from 'auto' to 'metadata' for better control
  }
  
  switch (priority) {
    case 'CRITICAL':
      return 'metadata';  // Changed from 'auto' - use metadata + programmatic load() for better control
    case 'HIGH':
      return 'metadata';  // Only metadata for above-fold
    case 'NORMAL':
    case 'LOW':
    case 'BACKGROUND':
    default:
      return 'none';      // No preload - lazy load when visible
  }
}

/**
 * Determine if video should autoplay based on priority
 */
export function shouldAutoplayForPriority(priority: MediaPriority | boolean | undefined): boolean {
  if (typeof priority === 'boolean') {
    return priority;
  }
  
  return priority === 'CRITICAL' || priority === 'HIGH';
}

/**
 * Determine if media should use eager loading (priority loading)
 */
export function shouldPriorityLoad(priority: MediaPriority | boolean | undefined): boolean {
  if (typeof priority === 'boolean') {
    return priority;
  }
  
  return priority === 'CRITICAL' || priority === 'HIGH';
}

export interface MediaConfig {
  // Video loading settings
  video: {
    maxRetries: number;
    retryDelay: number;
    timeoutMs: number;
    preloadStrategy: 'auto' | 'metadata' | 'none';
    autoplay: boolean;
    loop: boolean;
    muted: boolean;
  };
  
  // Image loading settings
  image: {
    preloadStrategy: 'eager' | 'lazy';
    priorityThreshold: number; // Images above fold with priority
  };
  
  // Cache settings
  cache: {
    duration: number; // Cache duration in ms (0 = indefinite)
    maxSize: number; // Max cached items
    useBrowserCache: boolean;
    useMemoryCache: boolean;
  };
  
  // Preloading settings
  preload: {
    allPages: boolean; // Preload all pages' media
    priorityOrder: 'current' | 'navigation' | 'all';
    backgroundPriority: 'low' | 'high';
    batchSize: number; // How many items to preload at once
    delayAfterCurrentPage: number; // Delay before starting background preload (ms)
  };
}

const isProduction = process.env.NODE_ENV === 'production';
const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

export const mediaConfig: MediaConfig = {
  video: {
    maxRetries: isProduction ? 3 : 2,
    retryDelay: isProduction ? 1500 : 1000,
    timeoutMs: isProduction ? 10000 : 8000,
    preloadStrategy: 'metadata', // Only preload metadata by default - reduces bandwidth
    autoplay: true,
    loop: true,
    muted: true,
  },
  
  image: {
    preloadStrategy: 'lazy',
    priorityThreshold: 2, // Only first 2 images (hero) get priority loading
  },
  
  cache: {
    duration: 0, // Indefinite - browser manages expiration
    maxSize: 200, // Reduced from 500 - less memory usage
    useBrowserCache: true,
    useMemoryCache: true,
  },
  
  preload: {
    allPages: false, // DISABLED - only preload current page's media to fix 88MB payload
    priorityOrder: 'current', // Only current page
    backgroundPriority: 'low',
    batchSize: isMobile ? 2 : 3, // Reduced batch size for better performance
    delayAfterCurrentPage: 5000, // Increased delay before any background preload
  },
};

/**
 * Get production-optimized video config
 */
export function getVideoConfig() {
  return mediaConfig.video;
}

/**
 * Get cache config
 */
export function getCacheConfig() {
  return mediaConfig.cache;
}

/**
 * Get preload config
 */
export function getPreloadConfig() {
  return mediaConfig.preload;
}

/**
 * Get video attributes for HTML video elements
 */
export function getVideoAttributes() {
  return {
    preload: mediaConfig.video.preloadStrategy,
    playsInline: true,
    muted: mediaConfig.video.muted,
    autoPlay: mediaConfig.video.autoplay,
    controls: false,
    'webkit-playsinline': 'true',
    crossOrigin: 'anonymous' as const,
  };
}

